import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import SpinningLoader from "../molecules/SpinningLoader";

export default function ResponsiveMedia({
  src,
  alt = "",
  className = "",
  style = {},
  isVideo = false,
  loop = true,
  muted = true,
  playsInline = true,
  onLoadedData,
  onClick,
}) {
  const [isMuted, setIsMuted] = useState(muted);
  const [showVolumeIndicator, setShowVolumeIndicator] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);

  const [useDefault] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const value = decodeURIComponent(params.get("useDefault") || "");
    console.log("useDefault param value:", value);
    return value.toLowerCase() === "true";
  });

  const transformVideoUrl = (url) => {
    console.log("Transforming URL:", url);
    console.log("useDefault value:", useDefault);

    if (useDefault === true) {
      console.log("Using original S3 URL");
      return url;
    }
    console.log("Using CloudFront URL");
    return url.replace(
      "https://ceimages.s3.amazonaws.com/",
      "https://d22uqmtf5jk3kz.cloudfront.net/"
    );
  };

  const transformedSrc = transformVideoUrl(src);

  const isVideoFile =
    isVideo ||
    transformedSrc?.match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/i) ||
    transformedSrc?.includes("video") ||
    transformedSrc?.includes("mp4");

  useEffect(() => {
    if (isVideoFile && videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.log("Video play error:", error);
        });
      }
    }
  }, [isVideoFile, transformedSrc]);

  const handleVideoClick = () => {
    if (!isVideoFile || !videoRef.current) return;

    if (isMuted) {
      videoRef.current.muted = false;
      setIsMuted(false);
    } else {
      videoRef.current.muted = true;
      setIsMuted(true);
    }

    setShowVolumeIndicator(true);
    setTimeout(() => {
      setShowVolumeIndicator(false);
    }, 2500);

    if (onClick) {
      onClick();
    }
  };

  const handleLoadedData = (e) => {
    setIsLoading(false);
    if (onLoadedData) {
      onLoadedData(e);
    }
  };

  if (isVideoFile) {
    return (
      <div className="fixed inset-4 overflow-hidden flex items-center justify-center bg-black">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <SpinningLoader />
          </div>
        )}
        <video
          ref={videoRef}
          src={transformedSrc}
          className={`max-w-full max-h-full object-contain ${className}`}
          autoPlay
          loop={loop}
          muted={isMuted}
          playsInline={playsInline}
          onLoadedData={handleLoadedData}
          onClick={handleVideoClick}
          style={style}
        />

        {showVolumeIndicator && (
          <div
            className="volume-indicator"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "rgba(0, 0, 0, 0.7)",
              borderRadius: "50%",
              width: "80px",
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 1000,
              animation: "fadeInOut 2.5s ease-in-out",
            }}
          >
            {isMuted ? (
              <svg width="35" height="35" viewBox="0 0 24 24" fill="white">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
              </svg>
            ) : (
              <svg width="35" height="35" viewBox="0 0 24 24" fill="white">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
              </svg>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-4 overflow-hidden flex items-center justify-center bg-black">
      <img
        src={transformedSrc}
        alt={alt}
        className={`max-w-full max-h-full object-contain ${className}`}
        loading="lazy"
        style={style}
        onClick={onClick}
      />
    </div>
  );
}

ResponsiveMedia.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  isVideo: PropTypes.bool,
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  playsInline: PropTypes.bool,
  onLoadedData: PropTypes.func,
  onClick: PropTypes.func,
};

ResponsiveMedia.defaultProps = {
  alt: "",
  className: "",
  style: {},
  isVideo: false,
  loop: true,
  muted: false,
  playsInline: true,
};
